// src/pages/ReadjudicacionMasiva/ReadjudicacionMasiva.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    CircularProgress,
    Box,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
    Add as AddIcon,
    Refresh as RefreshIcon,
    ViewModule as ViewModuleIcon,
    ViewList as ViewListIcon,
    Gavel as GavelIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import useReadjudicacion from '../../hooks/useReadjudicacion';
import { usePermisos } from '../../utils/usePermisos';
import readjudicacionService from '../../services/readjudicacionService';
import LotesFilters from '../../components/ReadjudicacionMasiva/Dashboard/LotesFilters';
import LoteCard from '../../components/ReadjudicacionMasiva/Dashboard/LoteCard';
import LotesTable from '../../components/ReadjudicacionMasiva/Dashboard/LotesTable';
import LoteTimeline from '../../components/ReadjudicacionMasiva/Dashboard/LoteTimeline';
import CrearLoteDialog from '../../components/ReadjudicacionMasiva/CrearLote/CrearLoteDialog';

const styles = {
    root: {
        paddingTop: '24px',
        paddingBottom: '24px'
    },
    header: {
        marginBottom: '24px'
    },
    title: {
        fontWeight: 700,
        color: '#5c6bc0'
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400
    },
    emptyBox: {
        textAlign: 'center',
        padding: '48px',
        color: 'rgba(0, 0, 0, 0.6)'
    },
    fab: {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 1000
    },
    fabRefresh: {
        position: 'fixed',
        bottom: '88px',
        right: '24px',
        zIndex: 1000
    },
    lotesGrid: {
        marginTop: '24px'
    },
    section: {
        marginTop: '32px',
        marginBottom: '24px'
    }
};

const ReadjudicacionMasiva = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { tienePermiso } = usePermisos();

    const {
        loading,
        lotes,
        totalLotes,
        metricas,
        listarLotes,
        obtenerMetricas,
        eliminarLote
    } = useReadjudicacion();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [filtros, setFiltros] = useState(() => {
        // Recuperar filtros del localStorage
        const savedFilters = localStorage.getItem('readjudicacion_filtros');
        if (savedFilters) {
            try {
                return JSON.parse(savedFilters);
            } catch (e) {
                return {
                    busqueda: '',
                    estado: '',
                    fechaDesde: '',
                    fechaHasta: ''
                };
            }
        }
        return {
            busqueda: '',
            estado: '',
            fechaDesde: '',
            fechaHasta: ''
        };
    });

    const [deleteDialog, setDeleteDialog] = useState({ open: false, lote: null });
    const [timelineDialog, setTimelineDialog] = useState({ open: false, estado: 0 });
    const [crearLoteDialog, setCrearLoteDialog] = useState(false);
    const [vistaMode, setVistaMode] = useState(() => {
        // Recuperar preferencia del localStorage
        return localStorage.getItem('readjudicacion_vista_mode') || 'list';
    });

    // Verificar permisos
    useEffect(() => {
        if (!tienePermiso('READJUDICACION MASIVA', 'SELECT')) {
            enqueueSnackbar('No tiene permisos para acceder a esta sección', { variant: 'error' });
            navigate('/');
        }
    }, [tienePermiso, navigate, enqueueSnackbar]);

    // Guardar filtros en localStorage cuando cambien
    useEffect(() => {
        localStorage.setItem('readjudicacion_filtros', JSON.stringify(filtros));
    }, [filtros]);

    // Cargar datos iniciales
    useEffect(() => {
        cargarDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cargarDatos = async (p = page, ps = pageSize) => {
        await Promise.all([
            listarLotes({ ...filtros, page: p, pageSize: ps }),
            obtenerMetricas()
        ]);
    };

    const handleBuscar = () => {
        setPage(1);
        listarLotes({ ...filtros, page: 1, pageSize });
    };

    const handleLimpiar = () => {
        const filtrosVacios = {
            busqueda: '',
            estado: '',
            fechaDesde: '',
            fechaHasta: ''
        };
        setFiltros(filtrosVacios);
        setPage(1);
        listarLotes({ ...filtrosVacios, page: 1, pageSize });
    };

    const handlePageChange = (e, newPage) => {
        setPage(newPage + 1);
        listarLotes({ ...filtros, page: newPage + 1, pageSize });
    };

    const handlePageSizeChange = (e) => {
        const newSize = parseInt(e.target.value);
        setPageSize(newSize);
        setPage(1);
        listarLotes({ ...filtros, page: 1, pageSize: newSize });
    };

    const handleVerLote = (lote) => {
        // Navegar según el estado del lote
        // Estado 0 = Pendiente Auditoría → página de auditoría
        // Estado 1 = Auditado → página de abogados (desapoderar)
        // Estado 2 = Desapoderado → página de abogados (asignar)
        // Estado 3 = Asignado (legacy) → página de pasaje
        // Estado 4 = Listos para Pasaje → página de pasaje (revisar y finalizar)
        // Estado 5 = Finalizado → página de pasaje (solo lectura)
        switch (lote.Estado) {
            case 0:
                navigate(`/readjudicacion-masiva/auditoria/${lote.Id}`);
                break;
            case 1:
            case 2:
                navigate(`/readjudicacion-masiva/abogados/${lote.Id}`);
                break;
            case 3:
            case 4:
            case 5:
                navigate(`/readjudicacion-masiva/pasaje/${lote.Id}`);
                break;
            default:
                navigate(`/readjudicacion-masiva/auditoria/${lote.Id}`);
        }
    };

    const handleEditarLote = (lote) => {
        handleVerLote(lote);
    };

    const handleEliminarLote = async () => {
        if (!deleteDialog.lote) return;

        const success = await eliminarLote(deleteDialog.lote.Id);
        if (success) {
            setDeleteDialog({ open: false, lote: null });
            cargarDatos();
        }
    };

    const handleCrearLote = () => {
        if (!tienePermiso('READJUDICACION MASIVA', 'INSERT')) {
            enqueueSnackbar('No tiene permisos para crear lotes', { variant: 'error' });
            return;
        }
        setCrearLoteDialog(true);
    };

    const handleCrearLoteConfirm = async (datosLote) => {
        try {
            const response = await readjudicacionService.crearLoteDesdeExpedientes(datosLote);

            if (response.ok) {
                enqueueSnackbar(`Lote ${response.idLote} creado correctamente con ${datosLote.expedientes.length} expedientes`, { variant: 'success' });
                cargarDatos();
                // Redirigir al lote creado
                navigate(`/readjudicacion-masiva/auditoria/${response.idLote}`);
            } else {
                enqueueSnackbar(response.mensaje || 'Error al crear lote', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error al crear lote:', error);
            enqueueSnackbar('Error al crear lote', { variant: 'error' });
        }
    };

    const handleRefresh = () => {
        cargarDatos();
    };

    const handleEstadoClick = (estado) => {
        const nuevosFiltros = {
            ...filtros,
            estado: estado.toString()
        };
        setFiltros(nuevosFiltros);
        setPage(1);
        listarLotes({ ...nuevosFiltros, page: 1, pageSize });
    };

    const handleVistaChange = (event, newMode) => {
        if (newMode !== null) {
            setVistaMode(newMode);
            localStorage.setItem('readjudicacion_vista_mode', newMode);
        }
    };

    // const handleShowTimeline = (estado) => {
    //     setTimelineDialog({ open: true, estado });
    // };

    return (
        <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 2, px: { xs: 2, md: 3 } }}>
        <Container maxWidth={false} disableGutters sx={{ pt: '8px', pb: '16px' }}>
            {/* Header compacto: título + stats + controles */}
            <Box sx={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px',
                mb: '12px',
            }}>
                <Box sx={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GavelIcon sx={{ color: '#5c6bc0', fontSize: 30 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#5c6bc0' }}>
                        Readjudicación Masiva
                    </Typography>
                </Box>

                {/* Stats inline clickeables */}
                {metricas && (
                    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Pend. Auditoría', value: metricas.pendientesAuditoria, color: '#f57c00', estado: 0 },
                            { label: 'Auditados', value: metricas.auditados, color: '#1976d2', estado: 1 },
                            { label: 'Desapoderados', value: metricas.desapoderados, color: '#7b1fa2', estado: 2 },
                            { label: 'Asignados', value: metricas.asignados, color: '#388e3c', estado: 3 },
                            { label: 'Listos Pasaje', value: metricas.listosParaPasaje, color: '#c62828', estado: 4 },
                            { label: 'Finalizados', value: metricas.finalizados, color: '#00897b', estado: 5 },
                        ].map(s => {
                            const activo = filtros.estado === s.estado.toString();
                            return (
                                <Box key={s.estado}
                                    onClick={() => handleEstadoClick(s.estado)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        bgcolor: activo ? s.color : 'white',
                                        color: activo ? 'white' : s.color,
                                        border: `1px solid ${s.color}`,
                                        borderRadius: '4px',
                                        px: '10px', py: '4px', cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        '&:hover': { bgcolor: activo ? s.color : `${s.color}18` },
                                    }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{s.value ?? 0}</Typography>
                                    <Typography sx={{ fontSize: 11 }}>{s.label}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Controles */}
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <ToggleButtonGroup value={vistaMode} exclusive onChange={handleVistaChange} size="small"
                        sx={{ bgcolor: 'white', '& .MuiToggleButton-root': { color: '#5c6bc0' }, '& .Mui-selected': { bgcolor: '#5c6bc0 !important', color: '#fff !important' } }}>
                        <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                    <Tooltip title="Actualizar">
                        <span><IconButton size="small" onClick={handleRefresh} disabled={loading}
                            sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: '4px' }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton></span>
                    </Tooltip>
                    {tienePermiso('READJUDICACION MASIVA', 'INSERT') && (
                        <Button variant="contained" size="small" startIcon={<AddIcon />}
                            onClick={handleCrearLote}
                            sx={{ bgcolor: '#5c6bc0', color: '#fff', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#3f51b5' } }}>
                            Nuevo Lote
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Filtros */}
            <LotesFilters
                filtros={filtros}
                onFiltrosChange={setFiltros}
                onBuscar={handleBuscar}
                onLimpiar={handleLimpiar}
            />

            {/* Listado de lotes */}
            {loading ? (
                <Box sx={styles.loadingBox}>
                    <CircularProgress />
                </Box>
            ) : lotes.length === 0 ? (
                <Box sx={styles.emptyBox}>
                    <Typography variant="h6" gutterBottom>No se encontraron lotes</Typography>
                    <Typography variant="body2" color="text.secondary">Ajusta los filtros o crea un nuevo lote</Typography>
                </Box>
            ) : vistaMode === 'grid' ? (
                <Grid container spacing={2} sx={{ mt: '8px' }}>
                    {lotes.map((lote) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={lote.Id}>
                            <LoteCard lote={lote} onVer={handleVerLote} onEditar={handleEditarLote} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Box sx={{ mt: '8px' }}>
                    <LotesTable
                        lotes={lotes}
                        onVer={handleVerLote}
                        onEliminar={(lote) => setDeleteDialog({ open: true, lote })}
                        puedeEliminar={tienePermiso('READJUDICACION MASIVA', 'DELETE')}
                        totalLotes={totalLotes}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </Box>
            )}

            {/* Dialog - Eliminar lote */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, lote: null })}
            >
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Está seguro que desea eliminar el lote {deleteDialog.lote?.Id}?
                    </Typography>
                    <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, lote: null })}>
                        Cancelar
                    </Button>
                    <Button onClick={handleEliminarLote} color="secondary" variant="contained">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog - Timeline */}
            <Dialog
                open={timelineDialog.open}
                onClose={() => setTimelineDialog({ open: false, estado: 0 })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Flujo de Readjudicación</DialogTitle>
                <DialogContent>
                    <LoteTimeline estadoActual={timelineDialog.estado} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTimelineDialog({ open: false, estado: 0 })}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog - Crear Lote */}
            <CrearLoteDialog
                open={crearLoteDialog}
                onClose={() => setCrearLoteDialog(false)}
                onCreate={handleCrearLoteConfirm}
            />
        </Container>
        </Box>
    );
};

export default ReadjudicacionMasiva;
